// T742 slice 1 — the DERIVED VIEW of every host that hands bytes to a person.
//
// THIS MODULE DECLARES NO HOST. It assembles, it cannot introduce (ADR-0025 decision 3).
// Every name it returns arrives from one of exactly three places, and each of the three
// already has an owner and a gate:
//
//   `~/config/hosts`            our own serving host           (T424, `lint-host-ssot`)
//   `~/config/external-origins` the origins we depend on       (T653, `lint-delivery-host-ssot`)
//   the deployment's env        the hosts that live in config  (see DELIVERY_HOST_ENV_VARS)
//
// ── Why a view and not a fourth registry ─────────────────────────────────────────
//
// The tempting design is one module listing every delivery host. That is how a codebase
// with three registries acquires a fourth, and ADR-0025 records it as rejected rather
// than quietly dropped. A view has no authority: delete a host from its owner and it
// disappears from here, from the monitor and from the CSP together. Add one here and
// nothing happens, because there is nowhere in this file to add it.
//
// ── What this closes ─────────────────────────────────────────────────────────────
//
// T730 shipped a block monitor on 2026-09-13 whose coverage was a hand-kept list inside
// the sampler, and within the hour T741 found a delivery surface it did not cover — the
// lead-magnet guide PDF, blocked at that moment, served from the host `R2_S3_ENDPOINT`
// names. That is not a gap in the list; it is the absence of anything forcing the list
// to be complete. Both consumers now derive from here and keep no list of their own
// (R-T742-3): `~/services/block-exposure.server` and
// `scripts/maintenance/check-block-exposure.mjs`.

// Relative, not `~/` — this module is reached by `functions/[[path]].ts`, which wrangler
// builds without the tsconfig path alias (T742 slice 3). Both targets sit in this folder and
// import nothing themselves, so the whole tree stays resolvable from either build.
import { EXTERNAL_ORIGINS, MEMBER_DELIVERY_ORIGIN_KEYS } from "./external-origins";
import { SERVING_HOST } from "./hosts";

/**
 * The environment variables that carry a delivery host — NAMES, never values.
 *
 * A host that arrives through configuration is invisible to both existing gates, because
 * each of them scans source and `wrangler.toml` is not source. Declaring the names here is
 * what lets a view assemble those hosts without claiming the right to choose one: the value
 * still lives only in the deployment config.
 *
 * The other URL-bearing variables are listed in `NON_DELIVERY_URL_ENV_VARS` below, each with
 * the reason it is not a delivery host. The two lists together are what make the
 * classification total; `R2_S3_ENDPOINT` moved from this one to that one in slice 2, and its
 * entry there records why that is a consequence and not an oversight.
 */
export const DELIVERY_HOST_ENV_VARS = ["MEDIA_PUBLIC_URL"] as const;

/**
 * The URL-bearing variables in `wrangler.toml` that are **not** delivery hosts, each with the
 * reason it is not — because "not a delivery host" is a claim, and an unexamined one is what
 * T741 was.
 *
 * ── Why a complement list exists at all (T742 slice 3) ───────────────────────────────
 *
 * A host that arrives through configuration is invisible to both source-scanning gates, so
 * before this list the only thing standing between a new URL variable and the T741 failure was
 * that somebody remembered to think about it. Nobody did, for `R2_S3_ENDPOINT`, on the day the
 * monitor shipped. Together with `DELIVERY_HOST_ENV_VARS` above, this makes the classification
 * TOTAL: `scripts/maintenance/lint-delivery-host-ssot.mjs` reads `wrangler.toml`, and a URL
 * variable in neither list fails the build until someone decides which it is.
 *
 * Neither list may be edited to make a gate quiet. Each entry is a sentence a reviewer reads.
 */
export const NON_DELIVERY_URL_ENV_VARS: Readonly<Record<string, string>> = {
  BETTER_AUTH_URL:
    "a LINK BASE, not an asset host — it hands over a page on our own zone, which `SERVING_HOST` " +
    "already covers in production, and its local value is a loopback address no intermediary can " +
    "block. Monitoring it would put a permanently-clean row into a monitor whose only job is to " +
    "be believed.",
  R2_S3_ENDPOINT:
    "an UPLOAD endpoint. It was a delivery host until 2026-09-14 — that is exactly what T741 was " +
    "— and the egress seam ended it: the only caller left is the Back Office presigning a PUT. " +
    "ADR-0025 § *Out of scope* excludes upload endpoints by name, because a block there is an " +
    "operator problem, not a member-facing outage, and an alarm nobody can act on is how a " +
    "monitor stops being read. `tests/unit/infra/742-entropy-accounting.test.ts` pins that no " +
    "module outside the signer reads it.",
  SENTRY_DSN:
    "an INGEST endpoint we write to, never a host a member's browser is sent to. A block there " +
    "costs us telemetry and costs her nothing; conflating the two is the same category error as " +
    "the upload endpoint above.",
};

export type DeliveryHostEnv = Partial<Record<(typeof DELIVERY_HOST_ENV_VARS)[number], string>>;

function hostnameOf(raw: string | undefined): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    // A malformed origin is a config bug the T730 zone gate catches at build time. A monitor
    // that throws on it stops monitoring the hosts that ARE well-formed, which is worse.
    return null;
  }
}

/** Our own host plus the declared origins that deliver bytes to a member's browser. */
function staticDeliveryHostnames(): string[] {
  return [
    SERVING_HOST.toLowerCase(),
    ...MEMBER_DELIVERY_ORIGIN_KEYS.map((key) => hostnameOf(EXTERNAL_ORIGINS[key])).filter(
      (h): h is string => h !== null,
    ),
  ];
}

/**
 * The delivery hosts, given the raw values of the declared env vars.
 *
 * Takes values rather than an env object so one code path serves both consumers: the Worker
 * has one environment in hand, the terminal script reads `wrangler.toml` and must check the
 * UNION across local, preview and production — a host is no less blocked for being configured
 * in an environment this process is not running in.
 *
 * Sorted and de-duplicated, so a caller can compare two runs without sorting first.
 */
export function deliveryHostnamesFrom(values: Iterable<string | undefined>): string[] {
  const hosts = new Set<string>(staticDeliveryHostnames());
  for (const value of values) {
    const host = hostnameOf(value);
    if (host) hosts.add(host);
  }
  return [...hosts].sort();
}

/** The delivery hosts for one running deployment. */
export function deliveryHostnames(env: DeliveryHostEnv): string[] {
  return deliveryHostnamesFrom(DELIVERY_HOST_ENV_VARS.map((name) => env[name]));
}
